const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const roundMoney = (value) => Math.round((toFiniteNumber(value) + Number.EPSILON) * 100) / 100;

export const resolveOrderShipping = (order = {}) => roundMoney(Math.max(
  0,
  toFiniteNumber(order.order_shipping ?? order.delivery_fee ?? 0),
));

export const calculateOrderItemSubtotal = (items = []) => roundMoney(
  (items || []).reduce((sum, item = {}) => {
    const quantity = toFiniteNumber(
      item.order_products_qty ?? item.qty ?? item.quantity,
    );
    const price = toFiniteNumber(
      item.order_products_price ?? item.price,
    );

    return sum + (quantity * price);
  }, 0),
);

export const getPersistedDiscountAmount = (order = {}) => {
  const explicitAmount = [order.discount_amount, order.discount]
    .map(toFiniteNumber)
    .find((value) => value > 0);

  if (explicitAmount) return roundMoney(explicitAmount);

  const totalBeforeDiscount = toFiniteNumber(order.order_total_without_discount);
  const total = toFiniteNumber(order.order_total);
  return roundMoney(Math.max(0, totalBeforeDiscount - total));
};

/**
 * Orders persist the applied discount amount. For legacy percentage discounts
 * there is no separate percentage-value column, so derive the original input
 * from the persisted amount and current item subtotal when reopening the edit
 * modal. This prevents an applied amount (for example, 40.80) from being
 * interpreted as 40.8 percent on the next save.
 */
export const resolvePersistedDiscount = (order = {}, items = []) => {
  const amount = getPersistedDiscountAmount(order);
  const type = String(order.discount_type || 'fixed').toLowerCase() === 'percentage'
    ? 'percentage'
    : 'fixed';

  if (amount <= 0) {
    return { enabled: false, type, value: 0, amount: 0 };
  }

  const explicitValue = toFiniteNumber(order.discount_value);
  const subtotal = calculateOrderItemSubtotal(items);
  const value = type === 'percentage'
    ? (explicitValue > 0 ? explicitValue : (subtotal > 0 ? (amount / subtotal) * 100 : 0))
    : amount;

  return {
    enabled: true,
    type,
    value: roundMoney(value),
    amount,
  };
};

export const calculateOrderEditTotals = ({
  items = [],
  applyDiscount = false,
  discountType = 'fixed',
  discountValue = 0,
  shipping = 0,
  isCollection = false,
} = {}) => {
  const subtotal = calculateOrderItemSubtotal(items);
  const requestedDiscount = !applyDiscount || toFiniteNumber(discountValue) <= 0
    ? 0
    : discountType === 'percentage'
      ? subtotal * (toFiniteNumber(discountValue) / 100)
      : toFiniteNumber(discountValue);
  const discount = roundMoney(Math.min(subtotal, Math.max(0, requestedDiscount)));
  const normalizedShipping = roundMoney(Math.max(0, toFiniteNumber(shipping)));

  const actualTotal = roundMoney(Math.max(0, subtotal + normalizedShipping - discount));
  const payableTotal = !isCollection && discount <= 0
    ? Math.max(25, actualTotal)
    : actualTotal;

  return {
    subtotal,
    discount,
    shipping: normalizedShipping,
    total: roundMoney(payableTotal),
  };
};
